// SimpleSwitch.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Form, Switch, Typography } from 'antd';
import { useSimpleFormContext } from './SimpleFormProvider';
import SimpleFormItem from './SimpleFormItem';
import translateX from 'utils/translateX';
import useCustomValidationStatus from 'hooks/useCustomValidationStatus';
import useFormElement from 'hooks/useFormElement';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleSwitch = ({ name, style = {}, disabled, subdocumentField, onChange, ...props }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    showLabel = false,
    checkedChildren = showLabel && translateX('yes'),
    unCheckedChildren = showLabel && translateX('no'),
    inline = false, // horizontal layout: label left, switch right
    ...formItemProps // Extract remaining props for FormItem (excluding Switch-specific props)
  } = props;
  const { locked, disabledFields, form } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;
  const { customValidationStatus, customValidationMessage, handleChange, checked } = useCustomValidationStatus({
    ...props,
    ...elementProps,
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  function handleSwitchChange(checked) {
    // Call internal handleChange first (updates form value)
    handleChange(checked);

    // Then call custom onChange if provided
    if (onChange) {
      onChange(checked, form);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────

  // Inline mode: label left + switch right, bound to form via Form.Item noStyle
  if (inline) {
    const label = formItemProps.label;
    return (
      <Flex justify="flex-end" align="center" gap={8}>
        {label && (
          <Typography.Text className="text-muted">
            {translateX(label)}
          </Typography.Text>
        )}
        <Form.Item name={fullFieldName} valuePropName="checked" noStyle>
          <Switch
            ref={elementRef}
            id={flattenFieldName}
            disabled={isDisabled}
            checkedChildren={checkedChildren}
            unCheckedChildren={unCheckedChildren}
            onChange={onChange}
            style={style}
          />
        </Form.Item>
      </Flex>
    );
  }

  // Default mode: vertical layout with SimpleFormItem
  return (
    <SimpleFormItem
      {...formItemProps}
      name={fullFieldName}
      elementProps={elementProps}
      customValidationStatus={customValidationStatus}
      customValidationMessage={customValidationMessage}
    >
      <Switch
        ref={elementRef}
        id={flattenFieldName}
        checked={checked}
        disabled={isDisabled}
        checkedChildren={checkedChildren}
        unCheckedChildren={unCheckedChildren}
        onChange={onChange ? handleSwitchChange : handleChange}
        style={style}
      />
    </SimpleFormItem>
  );
};

export default SimpleSwitch;
